import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { Workshop } from '@chancenwerk/shared/workshops/entities/Workshop.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs';
import '../components/akademie-component-workshop-entry.js';
import '../components/akademie-component-workshop-migration-onboarding.js';
import '../components/akademie-component-workshop-migration.js';
import '../components/akademie-component-workshop-rag-by-state.js';
import '../components/akademie-component-workshop-rag-by-workshop.js';
import '../components/akademie-component-workshop-reporting.js';
import { AkademieDialogWorkshopEdit } from '../components/dialogs/akademie-dialog-workshop-edit.js';

/**
 * @customElement akademie-page-workshops
 */
@customElement('akademie-page-workshops')
export class AkademiePageWorkshops extends CrPage {
  @state() private readonly _workshops = new RXController(
    this,
    subscribeAllEntities(Workshop)(Workshop.allFields).pipe(
      map(w => {
        return w.sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0));
      }),
    ),
  );
  @state() private _tab: 'Übersicht' | 'TtT-Statistik' | 'Migration' = 'Übersicht';

  constructor() {
    super();

    this.label = 'Workshops';
    this.description = 'Verwaltung der Workshops';
    this.permission = ChancenPermission.akademie.EDIT_WORKSHOP;
  }

  override header() {
    return html`<cr-button @click=${() => AkademieDialogWorkshopEdit.showPopup()}>Erstellen</cr-button>`;
  }

  override content() {
    return html`${this._getTabs()}
    ${{
      Übersicht: () => this._overview(),
      Migration: () => this._migration(),
      'TtT-Statistik': () =>
        html`<cr-flex padding="xs">
          <cr-card padding="md" space="md">
            <akademie-component-workshop-reporting
              .additionalColumns=${['Workshop']}
            ></akademie-component-workshop-reporting>
          </cr-card>
        </cr-flex>`,
    }[this._tab]()}`;
  }

  private _getTabs() {
    return html`
      <cr-tabs .selected=${['Übersicht', 'Reporting', 'Migration'].indexOf(this._tab)}>
        <cr-tab @click=${() => (this._tab = 'Übersicht')}> Übersicht </cr-tab>
        <cr-tab @click=${() => (this._tab = 'TtT-Statistik')}> TtT-Statistik </cr-tab>
        <cr-tab @click=${() => (this._tab = 'Migration')}> Migration </cr-tab>
      </cr-tabs>
    `;
  }

  private _migration() {
    return html`<cr-flex padding="sm" space="md">
      <akademie-component-workshop-migration
        .workshops=${this._workshops.value}
      ></akademie-component-workshop-migration>
      <akademie-component-workshop-migration-onboarding
        .workshops=${this._workshops.value}
      ></akademie-component-workshop-migration-onboarding>
    </cr-flex>`;
  }

  private _overview() {
    const options = buildChancenDataViewOptions<typeof Workshop>(
      {
        class: Workshop,
        itemSpacing: this.spacing.md,
        paginationComplete: true,
        view: new View({ filter: new AdornisFilter({ aspects: [] }) }),
        onlyShowLastTranslation: true,
        customColumns: [
          {
            columnName: '',
            customID: 'actions',
            columnWidth: 'max-content',
            getFieldMapping: (ent: Workshop) => {
              return html`<cr-button mode="icon" icon="visibility" href=${`/workshop/${ent._id}`}></cr-button>`;
            },
          },
          {
            columnName: 'Titel',
            fieldName: 'title',
            getFieldMapping: (ent: Workshop) =>
              (ent.title?.length ?? 0) > 50 ? ent.title?.slice(0, 50) + '...' : ent.title,
          },
          {
            columnName: 'Typ',
            fieldName: 'requiresTraining',
            getFieldMapping: (ent: Workshop) => (ent.requiresTraining ? 'TtT-Schulung' : 'Optional'),
          },
          { columnName: 'Zielgruppen', fieldName: 'roles' },
          { columnName: 'Aktiv', fieldName: 'active', columnWidth: 'max-content' },
          { columnName: 'Einarbeitung', fieldName: 'showAsOnboarding', columnWidth: 'max-content' },
        ],
      } as DataViewOptions<typeof Workshop>,
      {
        fieldAspectGeneration: {
          hideFieldsWithoutTranslation: true,
          whitelist: { title: 1, description: 1, requiresTraining: 1, active: 1, showAsOnboarding: 1 },
        },
      },
    );

    return html`
      <cr-card padding="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })} margin="sm">
        ${FilterEditor({ options: options.filterOptions })}
      </cr-card>
      <cr-flex ${css({ overflow: 'auto' })}>
        <cr-data-view ${css({ marginTop: this.spacing.sm })} .options=${options.dataViewOptions}> </cr-data-view>
      </cr-flex>
    `;
  }
}
